import {type IconName} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import Color from 'color';
import React, {
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {type ColorResult, SketchPicker} from 'react-color';
import {Icon, Popup} from 'semantic-ui-react';

import {
  ColorPaletteOption,
  getColorName,
  getColorPaletteSpectrumOrder,
} from '../util/colors';
import {DD_PRIVACY_ALLOW} from '../util/datadog/privacy';

export type ColorPickerTriggerRenderProps = {
  activeColor: string;
  icon: IconName;
  onBlur: () => void;
  onClick: () => void;
  onFocus: () => void;
  onMouseLeave: () => void;
  onMouseEnter: () => void;
};

type TriggerRenderDelegate = (
  params: ColorPickerTriggerRenderProps
) => ReactElement;

type ColorPickerTriggerProps = ColorPickerTriggerRenderProps & {
  invertColorsInDarkMode: boolean;
  readOnly: boolean;
  style?: CSSProperties;
  trigger: 'icon' | 'button' | TriggerRenderDelegate;
};

function ColorPickerTrigger({
  activeColor,
  icon,
  invertColorsInDarkMode,
  readOnly,
  style,
  trigger,
  ...handlers
}: ColorPickerTriggerProps) {
  if (trigger === 'icon') {
    return (
      <Icon
        data-test="color-picker-trigger"
        style={{
          ...style,
          opacity: 1,
          color: activeColor,
          cursor: readOnly ? 'default' : 'pointer',
        }}
        name="circle"
        title={getColorName(activeColor) || activeColor}
        {...handlers}
      />
    );
  }
  if (trigger === 'button') {
    return (
      // The Button component's `icon` prop can only take an IconName (not a semantic-ui icon). I can
      // use the semantic-ui icon as a child like <Button><Icon /></Button> but the icon's width and height are weird
      // and difficult to center.
      // I tried using the IconFilledCircle component as a child too but it has its own internal padding,
      // which makes it difficult to get the size right.
      // I also tried just passing in `filled-circle` as the icon prop, but the `Button` component doesn't allow
      // me to style a passed in icon either.
      // So I'm just using regular divs and making a circle shape.
      <div
        className="flex h-32 w-32 items-center justify-center rounded bg-transparent hover:bg-oblivion/[0.07] dark:hover:bg-moonbeam/[0.09]" // Same background color as `Button` in ghost variant on hover.
        role="button"
        {...handlers}>
        <div
          title={getColorName(activeColor) || activeColor}
          data-test="color-picker-trigger"
          className="h-12 w-12 rounded-full"
          style={{backgroundColor: activeColor}}
        />
      </div>
    );
  }
  return trigger({activeColor, icon, ...handlers});
}

/**
 * react-color tags changes it treats as discrete: 'hex' from a preset swatch or the hex
 * field, 'rgb' from the number fields. Saturation and hue drags carry no source at all,
 * and react-color debounces those rather than waiting for mouseup, so a single drag can
 * call `setColor` more than once.
 */
export type ColorChangeSource = 'hex' | 'rgb';

type SourcedColorResult = ColorResult & {source?: ColorChangeSource};

export interface ColorPickerProps {
  activeColor: string;
  colorPaletteOption?: ColorPaletteOption;
  containerStyle?: CSSProperties;
  footer?: ReactNode;
  invertColorsInDarkMode?: boolean; // whether to keep the colors the same in dark mode, or invert them.
  onGradientColorInteractionEnd?: () => void;
  onOpen?: () => void;
  readOnly?: boolean; // don't open on click
  setColor?: (
    color: string,
    source?: ColorChangeSource,
    shouldTrack?: boolean
  ) => void;
  style?: CSSProperties;
  trigger?: 'button' | 'icon' | TriggerRenderDelegate;
  triggerIcon?: IconName;
}

const ColorPicker = (props: ColorPickerProps) => {
  const {
    activeColor,
    colorPaletteOption,
    containerStyle,
    footer,
    invertColorsInDarkMode = true,
    onGradientColorInteractionEnd,
    onOpen,
    readOnly,
    setColor,
    style,
    trigger = 'icon',
    triggerIcon = 'circle-filled',
  } = props;
  const gradientInteractionActive = useRef(false);
  const endGradientColorInteraction = useCallback(() => {
    if (!gradientInteractionActive.current) {
      return;
    }

    gradientInteractionActive.current = false;
    onGradientColorInteractionEnd?.();
  }, [onGradientColorInteractionEnd]);

  useEffect(() => {
    window.addEventListener('mouseup', endGradientColorInteraction);
    window.addEventListener('touchend', endGradientColorInteraction);
    return () => {
      window.removeEventListener('mouseup', endGradientColorInteraction);
      window.removeEventListener('touchend', endGradientColorInteraction);
    };
  }, [endGradientColorInteraction]);

  const handleChange = (color: ColorResult) => {
    if ((color as SourcedColorResult).source == null) {
      gradientInteractionActive.current = true;
    }
  };

  const handleChangeComplete = (color: ColorResult) => {
    const {a, ...colorMinusAlpha} = color.rgb;
    const alpha = a || 0.8;
    const updatedColor = Color(colorMinusAlpha).alpha(alpha).string();
    if (setColor != null) {
      const source = (color as SourcedColorResult).source;
      setColor(updatedColor, source, source != null);
    }
  };

  const effectiveReadOnly = readOnly ?? false;
  const presetColors = [
    ...getColorPaletteSpectrumOrder(
      colorPaletteOption ?? ColorPaletteOption.DEFAULT
    ),
  ];
  // trigger popup recalculation when footer content changes
  const popperDependencies = [footer != null];
  const picker = invertColorsInDarkMode ? (
    <SketchPicker
      color={activeColor}
      presetColors={presetColors}
      styles={{
        default: {
          picker: {
            boxShadow: 'none',
          },
        },
      }}
      onChange={handleChange}
      onChangeComplete={handleChangeComplete}
    />
  ) : (
    <SketchPicker
      className="text-moon-600 dark:text-moon-200 [&_input]:bg-inherit [&_input]:text-inherit [&_label]:!text-inherit"
      styles={{
        default: {
          picker: {
            backgroundColor: 'inherit',
            boxShadow: 'none',
          },
        },
      }}
      color={activeColor}
      presetColors={presetColors}
      onChange={handleChange}
      onChangeComplete={handleChangeComplete}
    />
  );
  const content =
    footer == null ? (
      invertColorsInDarkMode ? (
        picker
      ) : (
        <TailwindContents>{picker}</TailwindContents>
      )
    ) : (
      <TailwindContents>
        <div className="overflow-hidden rounded-[8px] bg-wb-surface text-[light-dark(var(--moon-600),_var(--moon-200))]">
          {picker}
          <div className="border-t border-solid border-moon-200 px-12 py-8 dark:border-moon-700">
            {footer}
          </div>
        </div>
      </TailwindContents>
    );

  return (
    <Popup
      // Color swatches/hex inputs only — no user content.
      data-dd-privacy={DD_PRIVACY_ALLOW}
      openOnTriggerClick={!effectiveReadOnly}
      trigger={
        // @ts-expect-error Missing props added by semantic-ui-react's Popup
        <ColorPickerTrigger
          activeColor={activeColor}
          icon={triggerIcon}
          invertColorsInDarkMode={invertColorsInDarkMode}
          readOnly={effectiveReadOnly}
          style={style}
          trigger={trigger}
        />
      }
      content={content}
      popperDependencies={popperDependencies}
      popperModifiers={{
        preventOverflow: {
          boundariesElement: 'viewport',
        },
      }}
      // needs higher zindex than body .ui.dimmer in Base.less
      style={{padding: 0, left: '-15px', ...containerStyle}}
      on="click"
      onOpen={onOpen}
    />
  );
};

export default ColorPicker;
